import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { founderAdminFunctions } from '../services/firebase';
import { resolveMerchMeta, MERCH_CATALOG, MerchItemDef } from '../services/merchCatalog';
import {
  QrCode,
  ShoppingBag,
  RefreshCw,
  Plus,
  Search,
  ExternalLink,
  Copy,
  Check,
  UserCheck,
  UserX,
  Trash2,
  Download,
  AlertCircle,
  BarChart3,
  Layers,
} from 'lucide-react';

export interface MerchQrItem {
  code: string;
  status: 'active' | 'unassigned' | 'revoked';
  type: string;
  batch?: string;
  note?: string;
  claimedUid: string | null;
  claimedUsername: string | null;
  claimedDisplayName: string | null;
  claimedPhotoURL: string | null;
  assignedBy?: 'admin' | 'user';
  assignedAt?: string | null;
  scanCount: number;
  lastScannedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface Props {
  founderPhone: string;
}

const getInitialSearchQuery = () => {
  if (typeof window === 'undefined') return '';
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('merchSearch') || sessionStorage.getItem('merch_search_query') || '';
  } catch {
    return '';
  }
};

const getInitialStatusFilter = (): 'all' | 'active' | 'unassigned' => {
  if (typeof window === 'undefined') return 'all';
  try {
    const params = new URLSearchParams(window.location.search);
    const val = params.get('merchStatus') || sessionStorage.getItem('merch_status_filter');
    if (val === 'active' || val === 'unassigned') return val;
  } catch {
    // Ignore
  }
  return 'all';
};

const MerchQrAdminPanel: React.FC<Props> = () => {
  const [items, setItems] = useState<MerchQrItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchQuery, setSearchQuery] = useState(getInitialSearchQuery);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'unassigned'>(getInitialStatusFilter);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      if (searchQuery) {
        url.searchParams.set('merchSearch', searchQuery);
        sessionStorage.setItem('merch_search_query', searchQuery);
      } else {
        url.searchParams.delete('merchSearch');
        sessionStorage.removeItem('merch_search_query');
      }
      if (statusFilter !== 'all') {
        url.searchParams.set('merchStatus', statusFilter);
        sessionStorage.setItem('merch_status_filter', statusFilter);
      } else {
        url.searchParams.delete('merchStatus');
        sessionStorage.removeItem('merch_status_filter');
      }
      window.history.replaceState({}, '', url.toString());
    } catch {
      // Ignore URL/sessionStorage failures
    }
  }, [searchQuery, statusFilter]);

  // Assign Modal / State
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [assignCode, setAssignCode] = useState('');
  const [assignUsername, setAssignUsername] = useState('');
  const [assignType, setAssignType] = useState('black_shirt_men');
  const [assignBatch, setAssignBatch] = useState('merch_v1');
  const [assignNote, setAssignNote] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Batch Generator Modal / State
  const [isBatchOpen, setIsBatchOpen] = useState(false);
  const [batchCount, setBatchCount] = useState('25');
  const [batchPrefix, setBatchPrefix] = useState('m');
  const [batchType, setBatchType] = useState('black_shirt_men');
  const [batchStartNum, setBatchStartNum] = useState('1');
  const [batchName, setBatchName] = useState('batch_print');
  const [generatingBatch, setGeneratingBatch] = useState(false);

  const isEndpointMissing = (err: any) => {
    const code = String(err?.code || '').toLowerCase();
    const message = String(err?.message || '').toLowerCase();
    return (
      code === 'not-found' ||
      code === 'functions/not-found' ||
      code === 'unimplemented' ||
      message.includes('not found') ||
      message.includes('not-found')
    );
  };

  // Helper: call admin function with fallback
  const callAdminAction = useCallback(async (action: string, payload: Record<string, any> = {}) => {
    try {
      const manageFn = httpsCallable<any, any>(founderAdminFunctions, 'manageMerchQrsAdmin');
      const res = await manageFn({ action, ...payload });
      if (res.data) return res.data;
    } catch (primaryErr: any) {
      // Fallback for individual function endpoints ONLY if manageMerchQrsAdmin is not yet deployed (not-found).
      // Ambiguous transport errors (unavailable, timeout, 500) must NEVER replay mutations to prevent duplicate execution.
      if (!isEndpointMissing(primaryErr)) {
        throw primaryErr;
      }
      if (action === 'list') {
        const fn = httpsCallable<any, any>(founderAdminFunctions, 'listMerchQrsAdmin');
        const res = await fn(payload);
        return res.data;
      }
      if (action === 'assign') {
        const fn = httpsCallable<any, any>(founderAdminFunctions, 'assignMerchQrAdmin');
        const res = await fn(payload);
        return res.data;
      }
      if (action === 'unassign') {
        const fn = httpsCallable<any, any>(founderAdminFunctions, 'unassignMerchQrAdmin');
        const res = await fn(payload);
        return res.data;
      }
      if (action === 'delete') {
        const fn = httpsCallable<any, any>(founderAdminFunctions, 'deleteMerchQrAdmin');
        const res = await fn(payload);
        return res.data;
      }
      if (action === 'batch_create') {
        const fn = httpsCallable<any, any>(founderAdminFunctions, 'batchCreateMerchQrsAdmin');
        const res = await fn(payload);
        return res.data;
      }
      throw primaryErr;
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let accumulated: MerchQrItem[] = [];
      let cursor: string | null = null;
      let hasMoreItems = true;

      while (hasMoreItems) {
        const data = await callAdminAction('list', {
          limit: 500,
          startAfter: cursor || undefined,
        });
        if (data?.ok && Array.isArray(data?.items)) {
          const existing = new Set(accumulated.map((i) => i.code));
          const newOnes = data.items.filter((i: MerchQrItem) => !existing.has(i.code));
          accumulated = [...accumulated, ...newOnes];
          cursor = data?.nextCursor || null;
          hasMoreItems = Boolean(data?.hasMore && cursor);
          if (data.items.length === 0) break;
        } else {
          setLoadFailed(true);
          return;
        }
      }

      setItems(accumulated);
      setNextCursor(cursor);
      setHasMore(hasMoreItems);
      setLoadFailed(false);
    } catch (err: any) {
      console.error('[MerchQrAdminPanel] Failed to load items:', err);
      setError(err?.message || 'Failed to fetch merch QR codes.');
      setItems([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [callAdminAction]);

  const loadMoreItems = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await callAdminAction('list', { limit: 500, startAfter: nextCursor });
      if (data?.ok && Array.isArray(data?.items)) {
        setItems((prev) => {
          const existingCodes = new Set(prev.map((i) => i.code));
          const newItems = data.items.filter((i: MerchQrItem) => !existingCodes.has(i.code));
          return [...prev, ...newItems];
        });
        setNextCursor(data?.nextCursor || null);
        setHasMore(Boolean(data?.hasMore));
      }
    } catch (err: any) {
      console.error('[MerchQrAdminPanel] Failed to load more items:', err);
      setError(err?.message || 'Failed to load more merch items.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Smart sequence number calculation for prefix
  const getNextAvailableNumber = useCallback((prefix: string, itemList: MerchQrItem[]) => {
    const cleanPrefix = prefix.trim().toLowerCase();
    let maxNum = 0;
    const regex = new RegExp(`^${cleanPrefix}(\\d+)$`, 'i');
    for (const it of itemList) {
      const match = it.code.match(regex);
      if (match) {
        const val = parseInt(match[1], 10);
        if (!isNaN(val) && val > maxNum) {
          maxNum = val;
        }
      }
    }
    return maxNum + 1;
  }, []);

  const openBatchModal = () => {
    const nextStart = getNextAvailableNumber(batchPrefix, items);
    setBatchStartNum(String(nextStart));
    setIsBatchOpen(true);
  };

  const handlePrefixChange = (newPrefix: string) => {
    setBatchPrefix(newPrefix);
    const nextStart = getNextAvailableNumber(newPrefix, items);
    setBatchStartNum(String(nextStart));
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignCode.trim() || !assignUsername.trim()) {
      setError('Please provide both merch code and username.');
      return;
    }
    setAssigning(true);
    setError('');
    setSuccessMsg('');
    try {
      const data = await callAdminAction('assign', {
        code: assignCode.trim(),
        username: assignUsername.trim().replace(/^@+/, ''),
        type: assignType.trim() || 'black_shirt_men',
        batch: assignBatch.trim() || undefined,
        note: assignNote.trim() || undefined,
      });

      if (data?.ok) {
        setSuccessMsg(`Linked merch #${assignCode.toUpperCase()} to @${assignUsername.replace(/^@+/, '')}!`);
        setIsAssignOpen(false);
        setAssignCode('');
        setAssignUsername('');
        setAssignNote('');
        await fetchItems();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to assign merch QR code.');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async (code: string) => {
    if (!window.confirm(`Unlink merch #${code.toUpperCase()}? It will stop redirecting until reassigned.`)) {
      return;
    }
    setError('');
    setSuccessMsg('');
    try {
      const data = await callAdminAction('unassign', { code });
      if (data?.ok) {
        setSuccessMsg(`Unlinked merch #${code.toUpperCase()}.`);
        await fetchItems();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to unlink merch.');
    }
  };

  const handleDelete = async (code: string) => {
    if (!window.confirm(`Permanently delete merch #${code.toUpperCase()}? This code will be permanently removed.`)) {
      return;
    }
    setError('');
    setSuccessMsg('');
    try {
      const data = await callAdminAction('delete', { code });
      if (data?.ok) {
        setSuccessMsg(`Deleted merch #${code.toUpperCase()}.`);
        await fetchItems();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to delete merch code.');
    }
  };

  const handleBatchGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    const count = parseInt(batchCount, 10);
    const startNum = parseInt(batchStartNum, 10);
    if (!count || count < 1) {
      setError('Count must be at least 1.');
      return;
    }
    setGeneratingBatch(true);
    setError('');
    setSuccessMsg('');
    try {
      const data = await callAdminAction('batch_create', {
        count,
        prefix: batchPrefix.trim() || 'm',
        type: batchType.trim() || 'black_shirt_men',
        startNumber: startNum || 1,
        batchName: batchName.trim() || 'batch_print',
      });

      if (data?.ok) {
        setSuccessMsg(`Generated ${data.count} new merch QR codes!`);
        setIsBatchOpen(false);
        await fetchItems();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to batch generate merch codes.');
    } finally {
      setGeneratingBatch(false);
    }
  };

  const baseUrl = useMemo(() => {
    if (typeof window !== 'undefined' && window.location?.origin) {
      const hostname = window.location.hostname;
      const isLocal =
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.startsWith('192.168.') ||
        hostname.startsWith('10.') ||
        hostname.endsWith('.local');
      if (isLocal) {
        return window.location.origin;
      }
    }
    return 'https://clero.money';
  }, []);

  const getMerchUrl = (code: string) => {
    return `${baseUrl}/t/${encodeURIComponent(code)}`;
  };

  const copyUrl = (code: string) => {
    const url = getMerchUrl(code);
    navigator.clipboard.writeText(url);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const downloadCsv = () => {
    if (!items.length) return;
    const escapeCsv = (val: any) => String(val ?? '').replace(/"/g, '""');
    const header = 'Code,Item Number,Scan URL,Status,Assigned Username,Type,Batch,Scan Count\n';
    const rows = items.map((it, idx) => {
      const scanUrl = getMerchUrl(it.code);
      const username = it.claimedUsername ? `@${it.claimedUsername}` : '';
      const meta = resolveMerchMeta(it.type);
      return `"${escapeCsv(it.code)}","${idx + 1}","${escapeCsv(scanUrl)}","${escapeCsv(it.status)}","${escapeCsv(username)}","${escapeCsv(meta.name)}","${escapeCsv(it.batch || '')}","${it.scanCount}"`;
    }).join('\n');

    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `clero-merch-qrs-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (statusFilter === 'active' && item.status !== 'active') return false;
      if (statusFilter === 'unassigned' && item.status !== 'unassigned') return false;
      if (typeFilter !== 'all' && resolveMerchMeta(item.type).key !== typeFilter) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      const meta = resolveMerchMeta(item.type);
      return (
        item.code.toLowerCase().includes(q) ||
        (item.claimedUsername && item.claimedUsername.toLowerCase().includes(q)) ||
        (item.claimedDisplayName && item.claimedDisplayName.toLowerCase().includes(q)) ||
        meta.name.toLowerCase().includes(q) ||
        (item.batch && item.batch.toLowerCase().includes(q)) ||
        (item.note && item.note.toLowerCase().includes(q))
      );
    });
  }, [items, statusFilter, typeFilter, searchQuery]);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((it) => it.status === 'active').length;
    const unassigned = total - active;
    const totalScans = items.reduce((sum, it) => sum + (it.scanCount || 0), 0);
    return { total, active, unassigned, totalScans };
  }, [items]);

  // Batch preview
  const batchPreviewText = useMemo(() => {
    const count = parseInt(batchCount, 10) || 1;
    const start = parseInt(batchStartNum, 10) || 1;
    const p = (batchPrefix || 'm').toLowerCase().trim();
    const end = start + count - 1;
    return `${p}${String(start).padStart(3, '0')} ... ${p}${String(end).padStart(3, '0')} (${count} codes)`;
  }, [batchCount, batchStartNum, batchPrefix]);

  const catalogOptions: MerchItemDef[] = Object.values(MERCH_CATALOG);

  return (
    <div className="space-y-6">
      {/* Top Banner & Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-500 ring-1 ring-emerald-500/20">
            <ShoppingBag className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-slate-900">Merch QR Codes</h2>
            <p className="text-sm text-slate-500">
              Official tees (<code className="font-mono text-emerald-600">{baseUrl.replace(/^https?:\/\//, '')}/t/:code</code>) that dynamically route scans to user profiles.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={fetchItems}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={openBatchModal}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Batch Generator
          </button>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={!items.length}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => {
              setAssignCode('');
              setAssignUsername('');
              setAssignType('black_shirt_men');
              setAssignNote('');
              setIsAssignOpen(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-600"
          >
            <UserCheck className="h-3.5 w-3.5" />
            Assign Merch
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-medium uppercase tracking-wider">Total Merch</span>
            <ShoppingBag className="h-4 w-4" />
          </div>
          <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{loadFailed ? '—' : stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between text-emerald-600">
            <span className="text-xs font-medium uppercase tracking-wider">Active Linked</span>
            <UserCheck className="h-4 w-4" />
          </div>
          <p className="mt-2 text-2xl font-bold tracking-tight text-emerald-600">{loadFailed ? '—' : stats.active}</p>
        </div>
        <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-medium uppercase tracking-wider">Unassigned</span>
            <UserX className="h-4 w-4" />
          </div>
          <p className="mt-2 text-2xl font-bold tracking-tight text-slate-700">{loadFailed ? '—' : stats.unassigned}</p>
        </div>
        <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between text-blue-600">
            <span className="text-xs font-medium uppercase tracking-wider">Total Scans</span>
            <BarChart3 className="h-4 w-4" />
          </div>
          <p className="mt-2 text-2xl font-bold tracking-tight text-blue-600">{loadFailed ? '—' : stats.totalScans}</p>
        </div>
      </div>

      {/* Feedback Messages */}
      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          <Check className="h-4 w-4 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Controls: Search, Types, and Status Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search code, username, or batch..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Merch Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter merch by type"
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm focus:border-emerald-500 focus:outline-none"
          >
            <option value="all">All 3 Renders</option>
            {catalogOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.shortName}
              </option>
            ))}
          </select>

          {/* Status Tabs */}
          <div className="inline-flex rounded-xl bg-slate-100 p-1 text-xs font-medium text-slate-600">
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              className={`rounded-lg px-3 py-1.5 transition ${
                statusFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'hover:text-slate-900'
              }`}
            >
              All ({items.length})
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('active')}
              className={`rounded-lg px-3 py-1.5 transition ${
                statusFilter === 'active' ? 'bg-white text-emerald-700 shadow-sm' : 'hover:text-slate-900'
              }`}
            >
              Linked ({stats.active})
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('unassigned')}
              className={`rounded-lg px-3 py-1.5 transition ${
                statusFilter === 'unassigned' ? 'bg-white text-slate-900 shadow-sm' : 'hover:text-slate-900'
              }`}
            >
              Unassigned ({stats.unassigned})
            </button>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="border-b border-slate-100 bg-slate-50/75 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3.5">Code</th>
                <th className="px-4 py-3.5">Shirt Render</th>
                <th className="px-4 py-3.5">Assigned Wearer</th>
                <th className="px-4 py-3.5 text-center">Scans</th>
                <th className="px-4 py-3.5">Last Scanned</th>
                <th className="px-4 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loadFailed ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-rose-600">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <p className="text-sm font-medium">Failed to load merch QR codes.</p>
                      <button
                        type="button"
                        onClick={fetchItems}
                        className="rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-800 transition hover:bg-rose-200"
                      >
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    {loading ? 'Loading merch...' : 'No merch items found. Generate a batch or assign one!'}
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const isLinked = item.status === 'active' && item.claimedUsername;
                  const targetUrl = getMerchUrl(item.code);
                  const meta = resolveMerchMeta(item.type);

                  return (
                    <tr key={item.code} className="hover:bg-slate-50/50 transition">
                      {/* Code */}
                      <td className="px-4 py-3.5 font-mono text-sm font-bold text-slate-900">
                        <div className="flex items-center gap-2">
                          <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
                            #{item.code.toUpperCase()}
                          </span>
                          <button
                            type="button"
                            onClick={() => copyUrl(item.code)}
                            title="Copy redirect URL"
                            className="text-slate-400 hover:text-slate-600"
                          >
                            {copiedCode === item.code ? (
                              <Check className="h-3.5 w-3.5 text-emerald-500" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* Shirt Render Preview */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-transparent flex items-center justify-center">
                            <img
                              src={meta.imageUri}
                              alt={meta.name}
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900 text-xs">
                              {meta.name}
                            </div>
                            <div className="text-[11px] text-slate-400 font-medium">
                              Batch: {item.batch || '—'} {item.note ? `· ${item.note}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Assigned Wearer */}
                      <td className="px-4 py-3.5">
                        {isLinked ? (
                          <div className="flex items-center gap-2.5">
                            {item.claimedPhotoURL ? (
                              <img
                                src={item.claimedPhotoURL}
                                alt={item.claimedUsername || ''}
                                className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"
                              />
                            ) : (
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-800">
                                {item.claimedUsername?.slice(0, 2).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="font-semibold text-slate-900">
                                @{item.claimedUsername}
                              </div>
                              {item.claimedDisplayName && (
                                <div className="text-xs text-slate-400">
                                  {item.claimedDisplayName}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                            Unassigned
                          </span>
                        )}
                      </td>

                      {/* Scan Count */}
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex min-w-[28px] items-center justify-center rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                          {item.scanCount || 0}
                        </span>
                      </td>

                      {/* Last Scanned */}
                      <td className="px-4 py-3.5 text-xs text-slate-500">
                        {item.lastScannedAt
                          ? new Date(item.lastScannedAt).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : 'Never'}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <a
                            href={targetUrl}
                            target="_blank"
                            rel="noreferrer"
                            title="Test QR destination in browser"
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1.5 text-slate-600 shadow-sm hover:bg-slate-50"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              setAssignCode(item.code);
                              setAssignUsername(item.claimedUsername || '');
                              setAssignType(meta.key);
                              setAssignBatch(item.batch || 'merch_v1');
                              setAssignNote(item.note || '');
                              setIsAssignOpen(true);
                            }}
                            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                          >
                            {isLinked ? 'Reassign' : 'Assign'}
                          </button>
                          {isLinked && (
                            <button
                              type="button"
                              onClick={() => handleUnassign(item.code)}
                              className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                            >
                              Unlink
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDelete(item.code)}
                            title="Delete merch code permanently"
                            className="rounded-lg border border-rose-200 bg-rose-50 p-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {hasMore && !loadFailed && (
          <div className="flex justify-center border-t border-slate-100 p-4">
            <button
              type="button"
              onClick={loadMoreItems}
              disabled={loadingMore}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
            >
              {loadingMore ? 'Loading more…' : 'Load More Merch Codes'}
            </button>
          </div>
        )}
      </div>

      {/* Modal: Assign Merch */}
      {isAssignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-emerald-500" />
                <h3 className="text-lg font-bold text-slate-900">
                  {assignCode ? `Assign Merch #${assignCode.toUpperCase()}` : 'Assign Merch'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAssignOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAssign} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Merch Code (Printed on QR / Tag)
                </label>
                <input
                  type="text"
                  placeholder="e.g. m001, t001, or 042"
                  value={assignCode}
                  onChange={(e) => setAssignCode(e.target.value)}
                  required
                  className="w-full rounded-xl border border-slate-200 p-2.5 font-mono text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Clero Username to Link
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-sm text-slate-400 font-bold">@</span>
                  <input
                    type="text"
                    placeholder="username (e.g. alex)"
                    value={assignUsername}
                    onChange={(e) => setAssignUsername(e.target.value)}
                    required
                    className="w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-3 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Merch Product Picker */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Shirt Style &amp; Render
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {catalogOptions.map((opt) => {
                    const isSelected = assignType === opt.key;
                    return (
                      <button
                        type="button"
                        key={opt.key}
                        onClick={() => setAssignType(opt.key)}
                        className={`flex flex-col items-center rounded-xl border p-2 text-center transition ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500/20'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <img
                          src={opt.imageUri}
                          alt={opt.name}
                          className="h-14 w-14 object-cover rounded-lg mb-1"
                        />
                        <span className="text-[11px] font-bold text-slate-900 leading-tight">
                          {opt.shortName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Batch Name
                  </label>
                  <input
                    type="text"
                    placeholder="merch_v1"
                    value={assignBatch}
                    onChange={(e) => setAssignBatch(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 p-2.5 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Note / Size
                  </label>
                  <input
                    type="text"
                    placeholder="Size L, Embroidered"
                    value={assignNote}
                    onChange={(e) => setAssignNote(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 p-2.5 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAssignOpen(false)}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={assigning}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-600 disabled:opacity-50"
                >
                  {assigning ? 'Linking...' : 'Save & Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Smart Batch Generator */}
      {isBatchOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <QrCode className="h-5 w-5 text-emerald-500" />
                <h3 className="text-lg font-bold text-slate-900">Smart Batch Generator</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsBatchOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg"
              >
                ✕
              </button>
            </div>

            <p className="mt-2 text-xs text-slate-500">
              Guaranteed collision-free generator. Never overwrites existing codes or erases scan analytics.
            </p>

            <form onSubmit={handleBatchGenerate} className="mt-4 space-y-4">
              {/* Product Selection */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Select Shirt Style
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {catalogOptions.map((opt) => {
                    const isSelected = batchType === opt.key;
                    return (
                      <button
                        type="button"
                        key={opt.key}
                        onClick={() => setBatchType(opt.key)}
                        className={`flex flex-col items-center rounded-xl border p-2 text-center transition ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500/20'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <img
                          src={opt.imageUri}
                          alt={opt.name}
                          className="h-12 w-12 object-cover rounded-lg mb-1"
                        />
                        <span className="text-[11px] font-bold text-slate-900 leading-tight">
                          {opt.shortName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Prefix
                  </label>
                  <input
                    type="text"
                    value={batchPrefix}
                    onChange={(e) => handlePrefixChange(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 p-2.5 font-mono text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={batchCount}
                    onChange={(e) => setBatchCount(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 p-2.5 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Start #
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={batchStartNum}
                    onChange={(e) => setBatchStartNum(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 p-2.5 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Dynamic Range Preview */}
              <div className="flex items-center gap-2 rounded-xl bg-emerald-50/70 border border-emerald-100 p-2.5 text-xs text-emerald-800">
                <Layers className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="font-mono font-medium">{batchPreviewText}</span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Batch Label (for manufacturer / export)
                </label>
                <input
                  type="text"
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 p-2.5 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              <div className="mt-6 flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsBatchOpen(false)}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={generatingBatch}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-600 disabled:opacity-50"
                >
                  {generatingBatch ? 'Generating...' : 'Create Batch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchQrAdminPanel;
